<script setup lang="ts">
import type { Column, Table } from '@tanstack/vue-table';
import type { FlatCampaign } from './schema';
import { computed, ref } from 'vue';

// Usando os componentes shadcn que você baixou
import { Badge } from 'dashboard/components-shadcn/new-york/ui/badge';
import { Button } from 'dashboard/components-shadcn/new-york/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator
} from 'dashboard/components-shadcn/new-york/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from 'dashboard/components-shadcn/new-york/ui/popover';
import { Separator } from 'dashboard/components-shadcn/new-york/ui/separator';

interface DataTableFacetedGroupProps {
  table: Table<FlatCampaign>;
  groupableColumns: Column<FlatCampaign, any>[];
  rowGrouping: string[];
  disabled?: boolean;
}

const props = withDefaults(defineProps<DataTableFacetedGroupProps>(), {
  disabled: false,
});

const emit = defineEmits<{
  'update:rowGrouping': [value: string[]]
}>();

// Maintain a reactive set of selected group columns
const selectedGroupColumns = ref(new Set<string>(props.rowGrouping));

// Compute available group options
const groupOptions = computed(() => {
  // Lista de colunas que devem ser excluídas
  const excludedColumns = ['inbox_name', 'scheduled_at'];

  const filtered = props.groupableColumns.filter(column => !excludedColumns.includes(column.id));

  return filtered.map(column => ({
    value: column.id,
    label:
      column.columnDef.name ||
      (typeof column.columnDef.header === 'string' ? column.columnDef.header : column.id),
  }));
});


// Toggle a group option
function toggleGroupOption(value: string) {
  const currentSelection = new Set(selectedGroupColumns.value);
  if (currentSelection.has(value)) {
    currentSelection.delete(value);
  } else {
    currentSelection.add(value);
  }
  selectedGroupColumns.value = currentSelection;
  updateGrouping();
}

// Clear all grouping
function clearGrouping() {
  selectedGroupColumns.value = new Set();
  updateGrouping();
}

// Update table grouping and emit change
function updateGrouping() {
  const groupArray = Array.from(selectedGroupColumns.value);
  props.table.setGrouping(groupArray);
  emit('update:rowGrouping', groupArray);
}

// Função para classes condicionais (substitui cn)
const classNames = (...classes: (string | undefined | boolean)[]) => {
  return classes.filter(Boolean).join(' ')
}
</script>

<template>
  <Popover>
    <PopoverTrigger as-child>
      <Button
        variant="outline"
        size="sm"
        :disabled="disabled"
        :class="[
          'h-8 border-b border-n-slate-6 bg-n-slate-6 hover:bg-n-slate-4 dark:hover:bg-slate-4 transition-colors',
          { 'opacity-50 cursor-not-allowed': disabled }
        ]"
      >
        <span class="i-lucide-plus-circle mr-2 h-4 w-4 text-n-slate-11"></span>
        <span class="text-n-slate-12">Agrupar por</span>
        <template v-if="selectedGroupColumns.size > 0">
          <Separator orientation="vertical" class="mx-2 h-4 bg-n-slate-6" />
          <Badge
            variant="secondary"
            class="rounded-sm px-1 font-normal lg:hidden bg-green-100 text-green-800 border-green-200"
          >
            {{ selectedGroupColumns.size }}
          </Badge>
          <div class="hidden space-x-1 lg:flex">
            <Badge
              v-if="selectedGroupColumns.size > 2"
              variant="secondary"
              class="rounded-sm px-1 font-normal bg-green-100 text-green-800 border-green-200"
            >
              {{ selectedGroupColumns.size }} selecionado(s)
            </Badge>
            <template v-else>
              <Badge
                v-for="option in groupOptions.filter(opt => selectedGroupColumns.has(opt.value))"
                :key="option.value"
                variant="secondary"
                class="rounded-sm px-1 font-normal bg-green-100 text-green-800 border-green-200"
              >
                {{ option.label }}
              </Badge>
            </template>
          </div>
        </template>
      </Button>
    </PopoverTrigger>

    <PopoverContent
      class="w-[220px] p-0 bg-n-slate-1 border-n-slate-6 shadow-lg rounded-lg overflow-hidden"
      align="start"
    >
      <div class="bg-n-slate-1">
        <Command class="bg-n-slate-1">

          <CommandList class="bg-n-slate-1 max-h-60 overflow-y-auto">

            <CommandGroup class="p-1 bg-n-slate-1">
              <CommandItem
                v-for="option in groupOptions"
                :key="option.value"
                :value="option.value"
                class="flex items-center px-2 py-2 text-sm bg-n-slate-1 hover:bg-n-slate-3 rounded-sm cursor-pointer transition-colors"
                @select.prevent="toggleGroupOption(option.value)"
              >
                <div
                  :class="classNames(
                    'mr-3 flex h-4 w-4 items-center justify-center rounded border transition-colors',
                    selectedGroupColumns.has(option.value)
                      ? 'bg-green-600 border-green-600 text-white'
                      : 'border-n-slate-7 hover:border-n-slate-8',
                  )"
                >
                  <span
                    v-if="selectedGroupColumns.has(option.value)"
                    class="i-lucide-check h-3 w-3"
                  ></span>
                </div>

                <span class="flex-1 text-n-slate-12">{{ option.label }}</span>
              </CommandItem>
            </CommandGroup>

            <template v-if="selectedGroupColumns.size > 0">
              <div class="border-t border-n-slate-4">
                <CommandGroup class="p-1 bg-n-slate-1">
                  <CommandItem
                    :value="{ label: 'Limpar agrupamento' }"
                    class="flex items-center justify-center px-2 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 cursor-pointer transition-colors bg-n-slate-1"
                    @select.prevent="clearGrouping"
                  >
                    <span class="i-lucide-x mr-2 h-3 w-3"></span>
                    Limpar agrupamento
                  </CommandItem>
                </CommandGroup>
              </div>
            </template>
          </CommandList>
        </Command>
      </div>
    </PopoverContent>
  </Popover>
</template>

<style scoped>
/* Garantir que as variáveis CSS do sistema funcionem */
:deep([data-radix-popover-content]) {
  background-color: rgb(var(--slate-1));
  border: 1px solid rgb(var(--slate-6));
  box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
  border-radius: 0.5rem;
  padding: 0;
  overflow: hidden;
}

/* Estilos para o input de pesquisa */
:deep([cmdk-input-wrapper]) {
  padding: 0.5rem 0.75rem !important;
  border-bottom: 1px solid rgb(var(--slate-4)) !important;
}

:deep([cmdk-input]) {
  height: 2rem !important;
  padding: 0 !important;
  font-size: 0.875rem !important;
}

/* Ícone de pesquisa */
:deep(.lucide-search) {
  color: rgb(var(--slate-10)) !important;
  width: 1rem !important;
  height: 1rem !important;
  margin-right: 0.5rem !important;
}

/* Dark mode para WhatsApp */
:deep(.dark [data-radix-popover-content]) {
  background-color: rgb(var(--slate-2));
  border: 1px solid rgb(var(--slate-8));
  box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.2), 0 4px 6px -4px rgb(0 0 0 / 0.2);
}

/* Remover outline padrão do Radix */
:deep([data-radix-popover-content]:focus-visible) {
  outline: none;
}

/* Smooth animations compatíveis com WhatsApp */
:deep([data-radix-popover-content][data-state="open"]) {
  animation: slideDownAndFade 200ms cubic-bezier(0.16, 1, 0.3, 1);
}

:deep([data-radix-popover-content][data-state="closed"]) {
  animation: slideUpAndFade 150ms cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes slideDownAndFade {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideUpAndFade {
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-4px);
  }
}
</style>
